import {
  IconBook,
  IconBook2,
  IconHome,
  IconMedal,
  IconMessage,
  IconPinned,
  IconSchool,
  IconSettings,
  IconShoppingBag,
  IconSignLeft,
  IconUpload,
  IconUser,
} from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLogout } from '../../api/hooks/useAuth'
import styles from '../../styles/common/Navbar.module.scss'
import NavItem from './NavItem'
import { Icon } from '../../assets/icons'
import BaseButton from './BaseButton'
import { useAuthStore } from '../../store/useAuthStore'

interface NavBarProps {
  userRole?: 'admin' | 'manager' | 'parents' | 'individual'
  isSidebarOpen?: boolean
}

const NavBar = ({ userRole = 'admin', isSidebarOpen }: NavBarProps) => {
  const location = useLocation()
  const [activePath, setActivePath] = useState('')
  const logout = useLogout()
  const navigate = useNavigate()
  const { user, isViewingAsManager, backToAdmin } = useAuthStore()

  useEffect(() => {
    setActivePath(location.pathname)
  }, [location])

  const handleClickItem = (path: string) => {
    setActivePath(path)
  }

  const handleItemActive = (itemPath: string) => {
    return activePath.toLowerCase().startsWith(itemPath.toLowerCase())
  }

  const handleLogout = () => {
    logout()
  }

  const handleBackToAdmin = () => {
    backToAdmin()
    navigate('/dashboard')
  }

  const adminNavigationItems = [
    {
      label: 'Overview',
      path: '/dashboard',
      icon: <Icon name="overview" size={16} />,
    },
    {
      label: 'Centers',
      path: '/centers',
      icon: <Icon name="center" size={16} />,
    },
    {
      label: 'Users',
      path: '/admin/users',
      icon: <Icon name="user" size={16} />,
    },
    {
      label: 'Lectures',
      path: '/lectures',
      icon: <Icon name="lecture" size={16} />,
    },
    // { label: 'Uploads', path: '/uploads', icon: <Icon name="data" size={16} /> },
    {
      label: 'Inquiries',
      path: '/Inquiries',
      icon: <Icon name="qna" size={16} />,
    },
    {
      label: 'Chapters',
      path: '/chapters',
      icon: <Icon name="chapter" size={16} />,
    },
    {
      label: 'Orders',
      path: '/orders',
      icon: <Icon name="orders" size={16} />,
    },
    {
      label: 'Promo Codes',
      path: '/promo-codes',
      icon: <Icon name="mark" size={16} />,
    },
    {
      label: 'Reports',
      path: '/admin/reports',
      icon: <Icon name="data" size={16} />,
    },
  ]

  const managerNavigationItems = [
    {
      label: 'Overview',
      path: '/dashboard/manager',
      icon: <Icon name="overview" size={16} />,
    },
    {
      label: 'Users',
      path: '/admin/users',
      icon: <Icon name="user" size={16} />,
    },
    {
      label: 'Inquiries',
      path: '/inquiries',
      icon: <Icon name="qna" size={16} />,
    },
    // { label: 'Uploads', path: '/uploads', icon: <Icon name="data" size={16} /> },
    // { label: 'Orders', path: '/orders', icon: <Icon name="orders" size={16} /> },
    {
      label: 'Reports',
      path: '/admin/reports',
      icon: <Icon name="data" size={16} />,
    },
  ]

  const parentsNavigationItems = [
    {
      label: 'Overview',
      path: '/dashboard/parents',
      icon: <Icon name="overview" size={16} />,
    },
    {
      label: 'Reports',
      path: '/my-reports',
      icon: <Icon name="data" size={16} />,
    },
    {
      label: 'Billing',
      path: '/billing/individual',
      icon: <Icon name="orders" size={16} />,
    },
    {
      label: 'Inquiries',
      path: '/inquiries',
      icon: <Icon name="qna" size={16} />,
    },
  ]

  const individualNavigationItems = [
    {
      label: 'Overview',
      path: '/dashboard/individual',
      icon: <Icon name="overview" size={16} />,
    },
    {
      label: 'Reports',
      path: '/my-reports',
      icon: <Icon name="data" size={16} />,
    },
    {
      label: 'Avatar Shop',
      path: '/avatar-shop',
      icon: <IconShoppingBag size={16} />,
    },
    {
      label: 'Badges',
      path: '/badge-collection',
      icon: <IconMedal size={16} />,
    },
    {
      label: 'Billing',
      path: '/billing/individual',
      icon: <Icon name="orders" size={16} />,
    },
    {
      label: 'Inquiries',
      path: '/inquiries',
      icon: <Icon name="qna" size={16} />,
    },
  ]

  const teacherNavigationItems = [
    {
      label: 'Uploads',
      path: '/uploads',
      icon: <Icon name="data" size={16} />,
    },
  ]

  const getNavigationItems = () => {
    if (!user) {
      return individualNavigationItems
    }
    if (user?.STATUS === 'A' && isViewingAsManager) {
      return managerNavigationItems
    }
    switch (user?.STATUS) {
      case 'A':
        return adminNavigationItems
      case 'M':
        return managerNavigationItems
      case 'P':
        return parentsNavigationItems
      case 'T':
        return teacherNavigationItems
      case 'O':
      case 'U':
        return individualNavigationItems
      default:
        return individualNavigationItems
    }
  }
  const navigationItems = getNavigationItems()
  const shouldShowBackButton = user?.STATUS === 'A' && isViewingAsManager
  const accountNavigationItems = [
    {
      label: 'Account',
      path: '/account',
      icon: <Icon name="account" size={16} />,
    },
    {
      label: 'Logout',
      path: '#',
      icon: <Icon name="user" size={16} />,
      onClick: handleLogout,
      isLogout: true,
    },
  ]

  return (
    <div
      className={`${styles.navSection} ${
        !isSidebarOpen ? styles.collapsed : ''
      }`}
    >
      <aside
        className={`${styles.navbar} ${!isSidebarOpen ? styles.collapsed : ''}`}
      >
        <nav className={styles.navContainer}>
          <ul className={styles.navList}>
            {navigationItems.map((navItem, index) => (
              <NavItem
                key={index}
                label={navItem.label}
                path={navItem.path}
                icon={navItem.icon}
                isActive={handleItemActive(navItem.path)}
                onClick={() => handleClickItem(navItem.path)}
              />
            ))}
          </ul>
        </nav>
        <div className={styles.accountSection}>
          <div className={styles.sectionTitle}>Account</div>
          <ul className={styles.navList}>
            {accountNavigationItems.map((navItem, index) => (
              <NavItem
                key={`account-${index}`}
                label={navItem.label}
                path={navItem.path}
                icon={navItem.icon}
                isActive={handleItemActive(navItem.path)}
                onClick={
                  navItem.isLogout
                    ? navItem.onClick
                    : () => handleClickItem(navItem.path)
                }
                isLogout={navItem.isLogout}
              />
            ))}
          </ul>
        </div>
        {shouldShowBackButton && (
          <div className={styles.backToAdminSection}>
            <button
              onClick={handleBackToAdmin}
              className={styles.backToAdminButton}
            >
              Back to Admin
            </button>
          </div>
        )}
      </aside>
    </div>
  )
}

export default NavBar
